<script lang="ts">
  import LayoutGrid, { Cell } from '@smui/layout-grid';
  import { marked } from 'marked';
  import { isMobileWidth } from '../../../../common/util';
  import Icon from '../../../common/components/Icon.svelte';
  import Text from '../../../common/components/Text.svelte';
  import Tag from '../../../common/components/tag/Tag.svelte';
  import PictureTag from '../../../common/lazy-components/PictureTag.svelte';
  import { getBlogDateFormat, getBlogImageAltTextFromUrl } from '../../services/blog-data-services';

  export let data: any;
  const isResponsive = isMobileWidth();

  import { onDestroy, onMount } from 'svelte';

  const entryTime = Date.now();
  let eventTriggered = false; // Flag to ensure the event fires only once

  let debounceTimer;

  function appendBlogTitleToContainer() {
    const titleContainer = document.getElementById('js-blog-detail-top-title-container');
    if (!titleContainer) return false;

    const storedTitleNode = (window as any).__EXTRA_BLOG_TITLE_NODE as HTMLElement | undefined;
    if (!storedTitleNode) return true;

    titleContainer.innerHTML = '';
    titleContainer.appendChild(storedTitleNode);
    return true;
  }

  function trackBlogSelectedEvent() {
    if (!eventTriggered) {
      eventTriggered = true;
      const exitTime = Date.now();
      const timeSpentInMinutes = ((exitTime - entryTime) / 60000).toFixed(2);
      if (window['ACC']?.mixpanel && data?._meta?.name) {
        window['ACC'].mixpanel.trackBlogPageViewed(
          'Blog Selected',
          data?._meta?.name,
          timeSpentInMinutes,
          data?.category[0]?.id
        );
      } else {
        console.warn('Mixpanel tracking failed: missing ACC, mixpanel, or data.');
      }
      if (window['ACC']?.extragtm && data?._meta?.name) {
        window['ACC'].extragtm.trackBlogSelected(data?._meta?.name, timeSpentInMinutes, data?.category[0]?.id);
      } else {
        console.warn('GTM tracking failed!');
      }
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden' && !debounceTimer) {
      debounceTimer = setTimeout(() => {
        trackBlogSelectedEvent();
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }, 100); // Small delay to debounce rapid visibility changes
    }
  }

  onMount(() => {
    appendBlogTitleToContainer();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', trackBlogSelectedEvent);
  });

  onDestroy(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', trackBlogSelectedEvent);
  });
</script>

{#if data}
  <section class="blog-detail-top-section">
    <LayoutGrid>
      <Cell class="blog-detail-top-text-container" spanDevices={{ desktop: 12, tablet: 8, phone: 4 }}>
        <div class="blog-detail-top-text">
          <div class="blog-detail-top-title-container">
            <div id="js-blog-detail-top-title-container"></div>
            <!-- <h1 class="blog-detail-top-title">
              {data?.title}
            </h1> -->
          </div>
          <div class="blog-detail-top-readtime-text">
            {#if data?.date}
              <div class="blog-published-div">
                <Icon glyph="icon-published-new" type="primary" desktopSize={28} />
                <Text key="extra.ampliance.blog.publish.text" args={[getBlogDateFormat(data?.date)]} />
              </div>
            {/if}
            {#if data?.readTime}
              <div class="blog-read-div">
                <Icon glyph="icon-watch-icon" type="primary" desktopSize={28} />
                <Text key="extra.ampliance.blog.readtime.text" args={[data?.readTime]} />
              </div>
            {/if}
          </div>
        </div>
      </Cell>
      <Cell class="blog-top-image-container" spanDevices={{ desktop: 12, tablet: 8, phone: 4 }}>
        <div class="blog-detail-image">
          <PictureTag
            image={isResponsive ? data?.image + '?fmt=auto&h=196' : data?.image + '?fmt=auto&h=496'}
            alt={getBlogImageAltTextFromUrl(data?.image)}
            width="100%"
          />
        </div>
        {#if data.category && data?.category.length}
          <div class="blog-top-tag-container">
            {#each data?.category as cat}
              <Tag label={cat?.category} backgroundColorCode="#FFECA1" textColorCode="#051F2F" />
            {/each}
          </div>
        {/if}
      </Cell>
      <Cell class="blog-top-description-container" spanDevices={{ desktop: 12, tablet: 8, phone: 4 }}>
        <div class="blog-detail-top-description-container">
          {#if data?.description}
            <p class="blog-detail-top-description">
              {@html marked(data?.description)}
            </p>
          {/if}
        </div>
      </Cell>
    </LayoutGrid>
  </section>
{/if}

<style lang="scss">
  @import '../../../common/styles/colors.new.scss';
  .blog-detail-top-section {
    padding: 60px 48px 80px 48px;
    background: $neutral-light-04;
    border: 1px solid $secondary-gray-02;
    @include desktop-max-width {
      padding: 24px 16px;
    }
    :global {
      .mdc-layout-grid {
        padding: 0;
        max-width: 1440px;
        margin-inline: auto;
      }
      .blog-top-image-container {
        position: relative;
        img {
          height: 496px;
          object-fit: cover;
          border-radius: 20px;
          @include desktop-max-width {
            height: 196px;
            border-radius: 12px;
            // object-fit: fill;
          }
        }
      }
    }
    .blog-detail-top-text {
      display: flex;
      justify-content: space-between;
      align-items: center;
      @include desktop-max-width {
        flex-direction: column;
        align-items: flex-start;
      }
      .blog-detail-top-title {
        font-size: 38px;
        font-weight: 700;
        line-height: 50px;
        color: $neutral-dark-01;
        @include desktop-max-width {
          font-size: 18px;
          line-height: 24px;
        }
      }
      .blog-detail-top-readtime-text {
        display: flex;
        gap: 16px;
        font-size: 18px;
        line-height: 20px;
        color: $neutral-dark-02;
        @include desktop-max-width {
          font-size: 10px;
          line-height: 16px;
        }
      }
    }
    .blog-detail-top-description {
      margin: 0;
      :global(p) {
        font-size: 20px;
        font-weight: 500;
        line-height: 26px;
        color: $neutral-dark-01;
        margin: 0;
        @include desktop-max-width {
          font-size: 14px;
          line-height: 16px;
        }
      }
    }

    .blog-top-tag-container {
      position: absolute;
      top: 16px;
      left: 16px;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      @include desktop-max-width {
        display: none;
      }
    }
  }
</style>
